/**
 * Statement navigation. Desktop/tablet: a scrollable rail grouped by
 * competency with a status dot per row. Phone: a compact competency strip
 * plus a "statement N of M" stepper.
 */
import React from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { reviewDesk } from '@/design-system';
import { useTranslation } from '@/hooks/useTranslation';
import type { EngagementLanguage } from '@/types/mentorEngagements';
import type { IcfCompetencyBlock } from '@/data/icfFormStatements';
import { mentorStyles } from '../shared';
import { isDone, type RailState, type ReviewItem } from './model';

export const WRAPUP_ID = '__wrapup__';

const DOT: Record<RailState, { bg: string; border: string; dashed?: boolean }> = {
  unrated: { bg: reviewDesk.card, border: reviewDesk.lineStrong },
  ai_suggested: { bg: reviewDesk.accentWash, border: reviewDesk.accent, dashed: true },
  confirmed: { bg: reviewDesk.accent, border: reviewDesk.accent },
  overridden: { bg: reviewDesk.amber, border: reviewDesk.amber },
  evidence_rejected: { bg: '#E0524A', border: '#E0524A' },
};

export function StatusDot({ state, size = 10 }: { state: RailState; size?: number }) {
  const tone = DOT[state];
  return <View style={{ width: size, height: size, borderRadius: size / 2, backgroundColor: tone.bg, borderWidth: 1.5, borderColor: tone.border, borderStyle: tone.dashed ? 'dashed' : 'solid' }} />;
}

interface Props {
  items: ReviewItem[];
  competencies: IcfCompetencyBlock[];
  language: EngagementLanguage;
  selectedId: string;
  phone: boolean;
  width: number;
  onSelect: (id: string) => void;
}

export function SofStatementRail({ items, competencies, language, selectedId, phone, width, onSelect }: Props) {
  const { t } = useTranslation();
  const doneFor = (code: string) => items.filter(item => item.competency === code && isDone(item.reviewState)).length;
  const totalFor = (code: string) => items.filter(item => item.competency === code).length;
  const allDone = items.every(item => isDone(item.reviewState));

  if (phone) {
    const index = items.findIndex(item => item.id === selectedId);
    const current = items[index];
    const activeCode = current?.competency ?? (selectedId === WRAPUP_ID ? WRAPUP_ID : null);
    return (
      <View style={styles.phoneWrap}>
        <View style={styles.strip}>
          {competencies.map(competency => {
            const active = activeCode === competency.code;
            const done = doneFor(competency.code) === totalFor(competency.code);
            return (
              <Pressable key={competency.code} accessibilityRole="button" accessibilityState={{ selected: active }}
                onPress={() => { const first = items.find(item => item.competency === competency.code); if (first) onSelect(first.id); }}
                style={({ pressed }) => [styles.stripChip, active && styles.stripChipActive, pressed && mentorStyles.pressed]}>
                <Text style={[styles.stripText, active && { color: '#FFFFFF' }, done && !active && { color: reviewDesk.success }]}>{competency.code}</Text>
              </Pressable>
            );
          })}
          <Pressable accessibilityRole="button" accessibilityState={{ selected: activeCode === WRAPUP_ID }} onPress={() => onSelect(WRAPUP_ID)}
            style={({ pressed }) => [styles.stripChip, activeCode === WRAPUP_ID && styles.stripChipActive, pressed && mentorStyles.pressed]}>
            <MaterialIcons name="flag" size={14} color={activeCode === WRAPUP_ID ? '#FFFFFF' : allDone ? reviewDesk.success : reviewDesk.inkSoft} />
          </Pressable>
        </View>
        <Text style={styles.stepper}>
          {current ? t('mentorEngagements.review.rail.stepper', { index: index + 1, total: items.length }) : t('mentorEngagements.review.rail.wrapup')}
        </Text>
      </View>
    );
  }

  return (
    <ScrollView style={[styles.rail, { width }]} contentContainerStyle={styles.railContent}>
      {competencies.map(competency => (
        <View key={competency.code} style={styles.group}>
          <View style={styles.groupHead}>
            <Text style={styles.groupTitle} numberOfLines={1}>{competency.code} · {language === 'tr' ? competency.title_tr : competency.title_en}</Text>
            <Text style={styles.groupCount}>{t('mentorEngagements.review.rail.reviewed', { done: doneFor(competency.code), total: totalFor(competency.code) })}</Text>
          </View>
          {items.filter(item => item.competency === competency.code).map(item => {
            const active = item.id === selectedId;
            const text = language === 'tr' ? item.statement.text_tr : item.statement.text_en;
            return (
              <Pressable key={item.id} testID={`sof_review_rail_${item.id}`} accessibilityRole="button" accessibilityState={{ selected: active }}
                accessibilityLabel={`${item.id} ${text}`} onPress={() => onSelect(item.id)}
                style={({ pressed }) => [styles.row, active && styles.rowActive, pressed && mentorStyles.pressed]}>
                <View testID={`sof_review_rail_state_${item.id}`} accessibilityLabel={item.railState} style={styles.dotSlot}><StatusDot state={item.railState} /></View>
                <Text style={[styles.rowId, active && { color: reviewDesk.accent }]}>{item.id}</Text>
                <Text style={[styles.rowText, active && { color: reviewDesk.ink }]} numberOfLines={1}>{text}</Text>
                {item.lowConfidence && item.reviewState === 'ai_suggested' ? <MaterialIcons name="priority-high" size={14} color="#8A5B00" accessibilityLabel={t('mentorEngagements.review.rail.lowConfidence')} /> : null}
              </Pressable>
            );
          })}
        </View>
      ))}
      <Pressable testID="sof_review_rail_wrapup" accessibilityRole="button" accessibilityState={{ selected: selectedId === WRAPUP_ID }} onPress={() => onSelect(WRAPUP_ID)}
        style={({ pressed }) => [styles.wrapup, selectedId === WRAPUP_ID && styles.rowActive, pressed && mentorStyles.pressed]}>
        <MaterialIcons name="flag" size={16} color={allDone ? reviewDesk.success : reviewDesk.inkSoft} />
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text style={[styles.rowText, { color: reviewDesk.ink, fontWeight: '800' }]}>{t('mentorEngagements.review.rail.wrapup')}</Text>
          <Text style={styles.wrapupHint} numberOfLines={1}>{t('mentorEngagements.review.rail.wrapupHint')}</Text>
        </View>
      </Pressable>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  rail: { borderRightWidth: 1, borderRightColor: reviewDesk.line, backgroundColor: reviewDesk.canvas },
  railContent: { paddingVertical: 12, paddingHorizontal: 10, gap: 14 },
  group: { gap: 2 },
  groupHead: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8, paddingHorizontal: 8, paddingBottom: 6 },
  groupTitle: { flex: 1, color: reviewDesk.ink, fontSize: 12, fontWeight: '800' },
  groupCount: { color: reviewDesk.muted, fontSize: 11, fontWeight: '700', fontVariant: ['tabular-nums'] },
  row: { flexDirection: 'row', alignItems: 'center', gap: 8, minHeight: 36, paddingHorizontal: 8, borderRadius: 10 },
  rowActive: { backgroundColor: reviewDesk.accentSoft },
  dotSlot: { width: 14, alignItems: 'center' },
  rowId: { color: reviewDesk.inkSoft, fontSize: 12, fontWeight: '800', minWidth: 34, fontVariant: ['tabular-nums'] },
  rowText: { flex: 1, color: reviewDesk.inkSoft, fontSize: 13, minWidth: 0 },
  wrapup: { flexDirection: 'row', alignItems: 'center', gap: 10, minHeight: 44, paddingHorizontal: 10, borderRadius: 10, borderTopWidth: 1, borderTopColor: reviewDesk.line, paddingTop: 10 },
  wrapupHint: { color: reviewDesk.muted, fontSize: 11 },
  phoneWrap: { gap: 6, paddingHorizontal: 16, paddingTop: 10, paddingBottom: 8, borderBottomWidth: 1, borderBottomColor: reviewDesk.line, backgroundColor: reviewDesk.canvas },
  strip: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  stripChip: { minHeight: 30, minWidth: 40, alignItems: 'center', justifyContent: 'center', paddingHorizontal: 10, borderRadius: 999, borderWidth: 1, borderColor: reviewDesk.line, backgroundColor: reviewDesk.card },
  stripChipActive: { backgroundColor: reviewDesk.accent, borderColor: reviewDesk.accent },
  stripText: { fontSize: 12, fontWeight: '800', color: reviewDesk.inkSoft },
  stepper: { color: reviewDesk.muted, fontSize: 12, fontWeight: '700' },
});
